import { useMemo } from 'react';
import { useAuth } from './useAuth';
import type { BusinessType } from './useModuleSettings';

// Industry-aware terminology mapping
// Returns context-appropriate labels based on the organization's business type

interface IndustryTerms {
  // Navigation & sections
  kitchenSection: string;
  kitchenDisplay: string;
  kitchenStations: string;
  floorPlan: string;

  // Order types
  orderTypeInStore: string;
  orderTypeTakeaway: string;
  orderTypeDelivery: string;
  orderTypeBar: string;

  // Products & catalog
  catalog: string;
  standardization: string;
  standardizationDescription: string;
  recipe: string;
  recipes: string;

  // Placeholders
  businessNamePlaceholder: string;

  // Descriptions
  floorPlanDescription: string;
  kitchenDescription: string;
  stationsDescription: string;
}

const FOOD_TERMS: IndustryTerms = {
  kitchenSection: 'KITCHEN',
  kitchenDisplay: 'Kitchen (KDS)',
  kitchenStations: 'KDS Stations',
  floorPlan: 'Floor Plan',
  orderTypeInStore: 'Dine-In',
  orderTypeTakeaway: 'Takeout',
  orderTypeDelivery: 'Delivery',
  orderTypeBar: 'Bar Tab',
  catalog: 'Menu',
  standardization: 'Standardization',
  standardizationDescription: 'Recipe costing and standardization',
  recipe: 'Recipe',
  recipes: 'Recipes',
  businessNamePlaceholder: 'My Restaurant',
  floorPlanDescription: 'Manage tables and seating layouts',
  kitchenDescription: 'Kitchen Display System for order preparation',
  stationsDescription: 'Kitchen routing and station configuration',
};

const RETAIL_TERMS: IndustryTerms = {
  kitchenSection: 'FULFILLMENT',
  kitchenDisplay: 'Order Queue',
  kitchenStations: 'Work Stations',
  floorPlan: 'Store Layout',
  orderTypeInStore: 'In-Store',
  orderTypeTakeaway: 'Pickup',
  orderTypeDelivery: 'Delivery',
  orderTypeBar: 'Special Order',
  catalog: 'Products',
  standardization: 'Standardization',
  standardizationDescription: 'Product assembly and costing',
  recipe: 'Assembly',
  recipes: 'Assemblies',
  businessNamePlaceholder: 'My Store',
  floorPlanDescription: 'Manage store zones and station layouts',
  kitchenDescription: 'Order fulfillment queue display',
  stationsDescription: 'Fulfillment routing and station configuration',
};

const GENERIC_TERMS: IndustryTerms = {
  kitchenSection: 'PREPARATION',
  kitchenDisplay: 'Preparation Display',
  kitchenStations: 'Work Stations',
  floorPlan: 'Floor Plan',
  orderTypeInStore: 'On-Site',
  orderTypeTakeaway: 'Pickup',
  orderTypeDelivery: 'Delivery',
  orderTypeBar: 'Custom',
  catalog: 'Products',
  standardization: 'Standardization',
  standardizationDescription: 'Product configuration and costing',
  recipe: 'Configuration',
  recipes: 'Configurations',
  businessNamePlaceholder: 'My Business',
  floorPlanDescription: 'Manage areas and layouts',
  kitchenDescription: 'Order preparation display',
  stationsDescription: 'Work routing and station configuration',
};

function getTermsForBusinessType(businessType: BusinessType | null | undefined): IndustryTerms {
  switch (businessType) {
    case 'restaurant':
    case 'qsr':
    case 'bar':
    case 'cafe':
      return FOOD_TERMS;
    case 'retail':
    case 'wholesale':
      return RETAIL_TERMS;
    case 'other':
    default:
      return GENERIC_TERMS;
  }
}

export function useIndustryTerms(): IndustryTerms {
  const { organization } = useAuth();
  const businessType = organization?.business_type as BusinessType | null;

  return useMemo(() => getTermsForBusinessType(businessType), [businessType]);
}

// Static version for contexts outside React components
export function getIndustryTerms(businessType: BusinessType | null | undefined): IndustryTerms {
  return getTermsForBusinessType(businessType);
}

export type { IndustryTerms };
